'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp'
import { Eye, EyeOff } from 'lucide-react'
import Link from 'next/link'
import { useAuth } from '@/contexts/auth-context'
import { authService } from '@/services/auth'
import { getAuthErrorMessage, isEmailNotConfirmedError } from '@/utils/auth-errors'
import { Logo } from '@/components/landing/logo'

type AuthStep = 'form' | 'otp'

export default function AuthPage() {
    const router = useRouter()
    const { signIn, loading: authLoading } = useAuth()
    const [step, setStep] = useState<AuthStep>('form')
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [otp, setOtp] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [resendCooldown, setResendCooldown] = useState(0)
    const [isNewUser, setIsNewUser] = useState(false)

    useEffect(() => {
        if (resendCooldown > 0) {
            const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000)
            return () => clearTimeout(timer)
        }
    }, [resendCooldown])

    const handleAuth = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')
        setLoading(true)

        try {
            // First, try to sign in
            const {
                user,
                session,
                error: signInError,
            } = await signIn({
                email,
                password,
            })

            if (signInError) {
                // Check if error is due to unconfirmed email
                if (isEmailNotConfirmedError(signInError)) {
                    // User exists but email not confirmed - send OTP
                    const { error: otpError } = await authService.signInWithOtp(email)
                    if (otpError) {
                        setError(getAuthErrorMessage(otpError))
                        setLoading(false)
                    } else {
                        setIsNewUser(false)
                        setStep('otp')
                        setResendCooldown(60)
                        setLoading(false)
                    }
                } else {
                    // Check if it's an invalid credentials error (could be new user or wrong password)
                    const errorMessage = signInError.message?.toLowerCase() || ''
                    if (
                        errorMessage.includes('invalid login credentials') ||
                        errorMessage.includes('invalid credentials')
                    ) {
                        // Try to sign up - this might be a new user
                        const { user: newUser, error: signUpError } = await authService.signUp({
                            email,
                            password,
                        })

                        if (signUpError) {
                            // If signup also fails, check if it's because user already exists
                            const signUpMessage = signUpError.message?.toLowerCase() || ''
                            if (
                                signUpMessage.includes('already registered') ||
                                signUpMessage.includes('already exists')
                            ) {
                                // User exists but password is wrong
                                setError(
                                    'Invalid email or password. Please check your credentials and try again.'
                                )
                                setLoading(false)
                            } else {
                                // Other signup error
                                setError(getAuthErrorMessage(signUpError))
                                setLoading(false)
                            }
                        } else if (newUser) {
                            // New user created successfully - show OTP screen
                            setIsNewUser(true)
                            setStep('otp')
                            setResendCooldown(60)
                            setLoading(false)
                        }
                    } else {
                        // Other sign-in error
                        setError(getAuthErrorMessage(signInError))
                        setLoading(false)
                    }
                }
            } else if (user) {
                // Sign-in successful
                if (!user.email_confirmed_at) {
                    // Email not confirmed - send OTP
                    const { error: otpError } = await authService.signInWithOtp(email)
                    if (otpError) {
                        setError(getAuthErrorMessage(otpError))
                        setLoading(false)
                    } else {
                        setIsNewUser(false)
                        setStep('otp')
                        setResendCooldown(60)
                        setLoading(false)
                    }
                } else if (session) {
                    // Email confirmed and session exists - redirect to dashboard
                    // Keep loading true during redirect to prevent double submissions
                    router.push('/dashboard')
                }
            }
        } catch {
            setError('An unexpected error occurred. Please check your connection and try again.')
            setLoading(false)
        }
    }

    const handleOtpVerification = async (e: React.FormEvent) => {
        e.preventDefault()

        if (otp.length !== 6) {
            setError('Please enter a valid 6-digit code.')
            return
        }

        setError('')
        setLoading(true)

        try {
            // Use appropriate type based on whether this is a new user
            const {
                user,
                session,
                error: verifyError,
            } = await authService.verifyOtp({
                email,
                token: otp,
                type: isNewUser ? 'signup' : 'email',
            })

            if (verifyError) {
                setError(getAuthErrorMessage(verifyError))
                setLoading(false)
                return
            }

            if (user && session) {
                // Email verified successfully
                if (isNewUser) {
                    // New user - session is active, redirect to dashboard
                    // Keep loading true during redirect to prevent double submissions
                    router.push('/dashboard')
                } else {
                    // Existing user - sign in with password to get proper session
                    const { error: signInError } = await signIn({
                        email,
                        password,
                    })

                    if (signInError) {
                        setError(getAuthErrorMessage(signInError))
                        setLoading(false)
                    } else {
                        // Redirect to dashboard after successful sign in
                        router.push('/dashboard')
                    }
                }
            }
        } catch {
            setError('Verification failed. Please try again.')
            setLoading(false)
        }
    }

    const handleResendOtp = async () => {
        if (resendCooldown > 0) return

        setError('')
        setLoading(true)

        try {
            const { error } = isNewUser
                ? await authService.resendOtp(email)
                : await authService.signInWithOtp(email)

            if (error) {
                setError(getAuthErrorMessage(error))
            } else {
                setResendCooldown(60)
            }
        } catch {
            setError('Failed to resend code. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    const handleGoogleAuth = async () => {
        setError('')
        setLoading(true)

        try {
            const { error } = await authService.signInWithOAuth('google')
            if (error) {
                setError(getAuthErrorMessage(error))
            }
        } catch {
            setError('Google authentication failed. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="min-h-screen  text-foreground flex flex-col font-sans">
            {/* Main Content */}
            <div className="flex-1 flex items-center justify-center">
                <div className="w-full max-w-sm p-4">
                    <div className="text-center mb-8">
                        <div className="flex items-center justify-center mx-auto mb-4 ">
                            <Logo />
                        </div>

                        <h1 className="text-2xl font-bold text-foreground mb-2">
                            {step === 'form' ? 'Sign in to your account' : 'Verify Your Email'}
                        </h1>
                        <p className="text-muted-foreground">
                            {step === 'form'
                                ? 'If you do not have an account, signing in will create one for you'
                                : `We sent a verification code to ${email}`}
                        </p>
                    </div>

                    {step === 'form' ? (
                        <div>
                            {/* Google Auth */}
                            <Button
                                onClick={handleGoogleAuth}
                                variant="outline"
                                className="w-full mb-4 py-3 border-border hover:bg-accent flex items-center justify-center gap-3"
                            >
                                <svg className="w-5 h-5" viewBox="0 0 24 24">
                                    <path
                                        fill="#4285F4"
                                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                    />
                                    <path
                                        fill="#34A853"
                                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                    />
                                    <path
                                        fill="#FBBC05"
                                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                    />
                                    <path
                                        fill="#EA4335"
                                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                    />
                                </svg>
                                Continue with Google
                            </Button>

                            {/* Divider */}
                            <div className="relative my-6">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-border" />
                                </div>
                                <div className="relative flex justify-center text-xs uppercase">
                                    <span className="bg-background px-2 text-muted-foreground">
                                        Or
                                    </span>
                                </div>
                            </div>

                            {/* Email Form */}
                            <form onSubmit={handleAuth} className="space-y-4">
                                <div>
                                    <Label
                                        htmlFor="email"
                                        className="text-sm font-medium text-foreground"
                                    >
                                        Email Address
                                    </Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="Enter your email"
                                        required
                                        className="mt-1"
                                    />
                                </div>

                                <div>
                                    <Label
                                        htmlFor="password"
                                        className="text-sm font-medium text-foreground"
                                    >
                                        Password
                                    </Label>
                                    <div className="relative mt-1">
                                        <Input
                                            id="password"
                                            type={showPassword ? 'text' : 'password'}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="Enter your password"
                                            required
                                            className="pr-10"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                        >
                                            {showPassword ? (
                                                <EyeOff className="h-4 w-4" />
                                            ) : (
                                                <Eye className="h-4 w-4" />
                                            )}
                                        </button>
                                    </div>
                                </div>

                                <div className="flex items-center justify-end">
                                    <Link
                                        href="/forgot-password"
                                        className="text-sm text-primary hover:underline"
                                    >
                                        Forgot password?
                                    </Link>
                                </div>

                                {error && <div className="text-destructive text-sm">{error}</div>}

                                <Button
                                    type="submit"
                                    disabled={loading || authLoading}
                                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground py-3"
                                >
                                    {loading || authLoading ? 'Processing...' : 'Continue'}
                                </Button>
                            </form>
                        </div>
                    ) : (
                        <div>
                            {/* OTP Form */}
                            <form onSubmit={handleOtpVerification} className="space-y-6">
                                <div className="flex flex-col items-center">
                                    <Label className="text-sm font-medium text-foreground mb-4">
                                        Verification Code
                                    </Label>
                                    <InputOTP
                                        maxLength={6}
                                        value={otp}
                                        onChange={(value) => setOtp(value)}
                                    >
                                        <InputOTPGroup>
                                            <InputOTPSlot index={0} />
                                            <InputOTPSlot index={1} />
                                            <InputOTPSlot index={2} />
                                            <InputOTPSlot index={3} />
                                            <InputOTPSlot index={4} />
                                            <InputOTPSlot index={5} />
                                        </InputOTPGroup>
                                    </InputOTP>
                                </div>

                                {error && (
                                    <div className="text-destructive text-sm text-center">
                                        {error}
                                    </div>
                                )}

                                <Button
                                    type="submit"
                                    disabled={loading || authLoading || otp.length !== 6}
                                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground py-3"
                                >
                                    {loading || authLoading ? 'Verifying...' : 'Verify & Continue'}
                                </Button>

                                <div className="text-center">
                                    <button
                                        type="button"
                                        onClick={handleResendOtp}
                                        disabled={resendCooldown > 0 || loading}
                                        className="text-sm text-muted-foreground hover:text-foreground hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {resendCooldown > 0
                                            ? `Resend code in ${resendCooldown}s`
                                            : "Didn't receive the code? Resend"}
                                    </button>
                                </div>

                                <div className="text-center">
                                    <button
                                        type="button"
                                        onClick={() => setStep('form')}
                                        className="text-sm text-muted-foreground hover:text-foreground hover:underline"
                                    >
                                        Back to authentication
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
